import { NvidiaIcon } from "@/components/Icons/NvidiaIcon";
import { DeviceStatusType } from "@/utils/device";
import React from "react";
import { NotificationType } from "./user";
import { AnalyticsEventType } from "@/constants/analytics";

export type DeviceIntelligence = {
  device_intelligence: {
    device_score: number;
    is_anti_fingerprinting: boolean;
    is_bot: boolean;
    is_emulated: boolean;
    is_incognito: boolean;
    is_proxy: boolean;
    is_tor: boolean;
    is_browser_spoofed: boolean;
    shield_id: string;
  };
  platform: string;
  session_id: string;
  timestamp: number;
  version: string;
};

export type LivePricing = {
  type: string;
  typeColor: string;
  typeIcon: typeof NvidiaIcon;
  name: string;
  availabilityCount: number;
  cost: string;
  costChange: number;
  busyPercentage: number;
  id: string;
};

export type OptionType = {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  count?: number;
  tooltip?: string;
};

export type SimpleClusterResponse = {
  id: string;
  status: string;
  name: string;
  served: string;
  remaining: string;
  brand_name: string;
  hardware_name: string;
  hardware_quantity: number;
  completed_percent: number;
  compute_minutes_remaining?: number;
  billing_model?: BillingModel;
};

export type SimpleCluster = {
  id: string;
  status: string;
  name: string;
  served: string;
  brandName: string;
  hardwareName: string;
  remaining: string;
  percentCompleted: number;
  computeMinutesRemaining?: number;
  billingModel?: BillingModel;
  statusColor: string;
  hardwareQuantity: number;
  statusMessage: string;
  statusLabel: string;
  statusValue: string;
  hardwareManufacturerIcon: typeof NvidiaIcon;
  hardwareManufacturer: string;
  hardwareManufacturerColor: string;
};

export type ClusterResponse = {
  cluster_id?: string;
  resource_name: string;
  resource_type?: string;
  resource_id: string;
  status: string;
  percent_completed: number;
  hardware_quantity: number;
  hardware_name: string;
  icon: string;
  brand_name: string;
  brand_info:
    | {
        brand_name: string;
        brand_icon: string;
      }
    | {
        brand_name: string;
        brand_icon: string;
      }[];
  jupyter_url?: string;
  vscode_url?: string;
  ray_dashboard_url?: string;
  compute_hours_served: string;
  compute_hours_remaining: string;
  started_at: string;
  is_mega_cluster: boolean;
  billing_model?: BillingModel;
};

export type Hardware = {
  hardwareManufacturerColor: string;
  hardwareManufacturerIcon: typeof NvidiaIcon;
  hardwareManufacturer: string;
};

export type Cluster = {
  id: string;
  type?: string;
  name: string;
  hasIncident: boolean;
  hardwareName: string;
  hardwareQuantity: number;
  computeHoursRemaining: "1 Hrs 40 Mins";
  computeHoursServed: "1 Hrs 40 Mins";
  percentCompleted: number;
  status: string;
  statusColor: string;
  statusMessage: string;
  statusLabel: string;
  statusValue: string;
  jupyterUrl?: string;
  vscodeUrl?: string;
  rayDashboardUrl?: string;
  startedAt: string;
  resourceType?: string;
  isMega?: boolean;
  billingModel?: BillingModel;
  hardwares?: Hardware[];
  href?: string;
} & Hardware;

/** How a cluster is charged: upfront for a fixed window, or hourly from credits as it runs. */
export type BillingModel = "duration" | "payg";

export type AccountBurnRate = {
  hourlyRate: number | null;
  clusterCount: number;
};

export type SimpleClusterDetailResponse = {
  total_gpus: number;
  total_containers: number;
  gpus_per_container?: number;
  hardware_name?: string;
  brand_name?: string;
  hardware_quantity: number;
  base_tier_name: string;
  started_at: string;
  id: number;
  k8s_file?: string;
  served: string;
  remaining: string;
  created_at: string;
  finishes_at: string;
  finished_at: string | null;
  download_speed_mbps: number;
  upload_speed_mbps: number;
  amount_paid: number;
  completed_percent: number;
  name: string;
  status: string;
  locations: {
    id: number;
    name: string;
    iso2: string;
  }[];
  hardware_info: {
    hardware_name: string;
    brand_name: string;
    quantity: number;
  }[];
  asset: "iocoin" | "usdc";
  total_cost: number;
  billing_model?: BillingModel;
  total_vms?: number;
  vm_image_type?: string;
  gpus_per_vm?: number;
  gpus_per_node?: number;
  github_ids?: string[];
  ssh_keys?: Record<string, string>;
  container_config?: {
    health_check?: HealthCheck | null;
  } | null;
};

export type SimpleClusterDetail = {
  id: number;
  served: string;
  k8sFile: string;
  percentCompleted: number;
  baseTierName: string;
  startedAt: string;
  hardwareQuantity: number;
  remaining: string;
  createdAt: string;
  finishesAt: string;
  finishedAt: string | null;
  downloadSpeedMbps: number;
  uploadSpeedMbps: number;
  amountPaid: number;
  name: string;
  hardwareName: string;
  status: string;
  locations: {
    value: string;
    label: string;
    code: string;
  }[];
  hardware: {
    name: string;
    brandName: string;
    hardwareQuantity: number;
  }[];
  currency: "iocoin" | "usdc";
  statusColor: string;
  statusMessage: string;
  statusLabel: string;
  statusValue: string;
  totalCost: number;
  billingModel?: BillingModel;
  resourceType?: string;
  isMega?: boolean;
  vmImageType?: string;
  gpusPerVm?: number;
  githubIds?: string[];
  sshKeys?: Record<string, string>;
  gpusPerContainer?: number;
  totalGpus?: number;
  totalContainers?: number;
  totalVms?: number;
  /** True when deployment container_config includes an HTTP health check path. */
  hasDeclaredHealthCheck?: boolean;
} & Hardware;

export type ClusterDetailResponse = {
  cluster_id: string;
  cluster_name: string;
  status: string;
  percent_completed: number;
  started_at: string;
  finished_at: string;
  ray_dashboard_url: string;
  vscode_url: string;
  compute_hours_served: string;
  compute_hours_remaining: string;
  locations: {
    location_id: number;
    location_name: string;
    iso2: string;
  }[];
  security_compliance: string;
  base_tier_name: string;
  download_speed_mbps: number;
  upload_speed_mbps: number;
  hardware_quantity: number;
  amount_paid: number;
  amount_refunded: number;
  jupyter_url?: string;
  dashboard_auth_key?: string;
  running_for?: string;
  hardware_name?: string;
  k8s_file: string;
  hardware_info?: {
    hardware_id: number;
    hardware_name: string;
    quantity: number;
  };
  brand_info: {
    brand_name: string;
    brand_icon: string;
  }[];
  currency: "iocoin" | "usdc";
  total_cost: number;
  resource_type: string;
  is_mega_cluster: boolean;
};

export type ClusterDetail = {
  totalGpus?: number;
  totalContainers?: number;
  gpusPerContainer?: number;
  totalVms?: number;
  amountPaid: number;
  amountRefunded: number;
  baseTierName: string;
  clusterId: string;
  clusterName: string;
  computeHoursRemaining: string;
  computeHoursServed: string;
  downloadSpeedMbps: number;
  finishedAt: string;
  hardwareQuantity: number;
  percentCompleted: number;
  rayDashboardUrl: string;
  securityCompliance: false;
  startedAt: string;
  status: string;
  statusColor: string;
  statusMessage: string;
  statusLabel: string;
  uploadSpeedMbps: number;
  vscodeUrl: string;
  locations: {
    value: string;
    label: string;
    code: string;
  }[];
  jupyterUrl: string;
  dashboardAuthKey: string;
  runningFor: string;
  hardwareName: string;
  k8sFile: string;
  hardwares?: Hardware[];
  currency: "iocoin" | "usdc";
  totalCost: number;
  resourceType?: string;
  isMega?: boolean;
} & Hardware;

export type ClusterWorkerInfoResponse = {
  total_gpu: number;
  total_cpu: number;
  workers_info: ClusterWorkerResponse[];
};

export type SimpleClusterWorkerInfoResponse = {
  total: number;
  workers: SimpleClusterWorkerResponse[];
  gpu_count?: number;
  cpu_count?: number;
};

export type ClusterWorkerInfo = {
  totalGpu: number;
  totalCpu: number;
  items: ClusterWorker[];
};

export type ContainerLocation = {
  id: number;
  name: string;
  iso2: string;
  available_replicas: number;
};

export type SimpleClusterLocation = {
  id: number;
  name: string;
  iso2: string;
  available: number;
};

export type SimpleClusterHardwareResponse = {
  id: number;
  name: string;
  brand_name: string;
  available: number;
};

export type DynamicSupplyHardwareResponse = {
  id: string;
  name: string;
  price: number;
  vram_per_card: number;
  interconnect: string;
  nvlink: boolean;
  storage: number;
  vcpu: number;
  memory: number;
  num_cards: number;
  location: string; // ISO 2 code
  supplier: "internal" | "external";
  old_price?: number;
  sold_out: boolean;
  deploy_id?: string;
  confidential_compute?: boolean;
};

export type DynamicSupplyHardwareFiltersResponse = {
  gpu_family: string[];
  gpu_memory: { min: number; max: number };
  regions: string[];
  cpu_cores: { min: number; max: number };
  device_memory: { min: number; max: number };
  storage: { min: number; max: number };
  supplier: ("internal" | "external")[];
  confidential_compute: boolean[];
};

export type DynamicSupplyHardware = {
  hardwareId: string;
  hardwareName: string;
  price: number;
  vramPerCard: number;
  interconnect: string;
  location: string; // ISO 2 code
  nvlink: boolean;
  storage: number;
  oldPrice?: number;
  numCards: number;
  vCPU: number;
  memory: number;
  supplier: "internal" | "external";
  isSoldOut: boolean;
  deploymentId?: string;
  isConfidentialCompute?: boolean;
};

export type SimpleClusterHardware = {
  id: number;
  name: string;
  brandName: string;
  hardwareManufacturerColor: string;
  hardwareManufacturerIcon: typeof NvidiaIcon;
  available: number;
  price?: number;
};

export type ContainerHardware = SimpleClusterHardware & {
  maxGpusPerContainer: number;
  minGpusPerContainer: number;
  network: number;
};

export type VirtualMachineHardware = SimpleClusterHardware & {
  minGpusPerVm: number;
  maxGpusPerVm: number;
  network: number;
};

export type KubernetesHardware = SimpleClusterHardware & {
  minGpusPerNode: number;
  maxGpusPerNode: number;
  network: number;
};

export type VirtualMachineHardwareResponse = {
  id: number;
  min_gpus_per_vm: number;
  max_gpus_per_vm: number;
  hardware_name: string;
  brand_name: string;
  hardware_id: number;
  available: number;
  price: number;
};

// Kubernetes node hardware response shape
export type KubernetesNodeHardwareResponse = {
  id: number;
  min_gpus_per_node: number;
  max_gpus_per_node: number;
  hardware_name: string;
  brand_name: string;
  hardware_id: number;
  available: number;
  price: number;
};

export type VmHardwareLocation = {
  id: number;
  name: string;
  iso2: string;
  available_replicas: number;
};

export type KubernetesHardwareLocation = {
  id: number;
  name: string;
  iso2: string;
  available_nodes: number;
};

export type SimpleClusterWorkerInfo = {
  total: number;
  items: SimpleClusterWorker[];
  totalGpu?: number;
  totalCpu?: number;
};

export type ExternalCheckStatus = "healthy" | "unhealthy" | "unknown";

export type SimpleClusterWorkerResponse = {
  id: number | string;
  ssh_connect?: string;
  public_url?: string;
  created_at?: string;
  ssh_link?: string;
  login?: string;
  password?: string;
  hardware: string;
  hardware_quantity: number;
  brand_name: string;
  status: string;
  container_id?: string;
  allocation_id?: string;
  uptime_percent?: number;
  container_events?: { time: string; message: string }[];
  vm_events?: { time: string; message: string }[];
  vm_id?: string;
  vm_status?: string;
  ssh_access?: string;
  network_services?: Record<string, WorkerNetworkServiceType>;
  is_external?: boolean;
  external_check_status?: ExternalCheckStatus | null;
};

export type ContainerHardwareResponse = {
  id: number;
  name: string;
  max_gpus_per_container: number;
  min_gpus_per_container: number;
  hardware_name: string;
  brand_name: string;
  hardware_id: number;
  available: number;
};

export type ClusterWorkerResponse = {
  device_id?: string;
  uptime_percent?: number;
  hardware_name: string;
  brand_name: string;
  brand_icon: string;
  hardware_quantity?: number;
  status: string;
  is_external?: boolean;
};

export type ClusterWorker = {
  id: string;
  deviceId: string;
  uptimePercent: number;
  hardwareName: string;
  hardwareManufacturerIcon: typeof NvidiaIcon;
  hardwareManufacturer: string;
  hardwareManufacturerColor: string;
  hardwareQuantity: number;
  status: string;
  isExternal?: boolean;
};

export type ContainerEvent = { time: string; message: string };

export type SimpleClusterWorker = {
  id: string;
  sshConnect: string;
  sshLink: string;
  login: string;
  password: string;
  brandName: string;
  hardwareName: string;
  hardwareManufacturerIcon: typeof NvidiaIcon;
  hardwareManufacturer: string;
  hardwareManufacturerColor: string;
  hardwareQuantity: number;
  status: string;
  containerId: string;
  allocationId?: string;
  publicUrl?: string | null;
  uptimePercent?: number;
  createdAt?: string;
  sshAccess: string;
  networkServices: Record<string, WorkerNetworkServiceType>;
  isExternal?: boolean;
  publicUrl?: string | null;
  containerEvents?: ContainerEvent[];
  externalCheckStatus?: ExternalCheckStatus | null;
};

export type SupplyHardware = {
  quantity: number;
  hardware_id: number;
  quantity_available: number;
};

export type CreateClusterDataFilteredResponse = {
  supplier_id: number;
  hardware_id: number;
  connectivity_tier: number;
  security_soc2: boolean;
  service_id: number;
  hardware_qty: number;
  location_id: string;
}[];

export type CreateClusterDataResponse = {
  securitySoc2Enabled?: boolean;
  suppliers: {
    id: number;
    name: string;
    icon: string;
    description: string;
    busy_percent: number;
    device_count: number;
  }[];
  hardwares: {
    hardware_id: number;
    type: string;
    name: string;
    hardware_quantity: number;
    price: number;
    brand_name: string;
    brand_icon: string;
    busy_percent: number;
  }[];
  locations: {
    id: number;
    name: string;
    icon: string;
    description: string;
    busy_percent: number;
    iso2: string;
  }[];
  connectivity_tiers: {
    connectivity_tier: number;
    name: string;
    download_speed_mbps: number;
    upload_speed_mbps: number;
  }[];
  services: {
    id: number;
    name: string;
    icon: string;
    description: string;
    disabled?: boolean;
  }[];
  durations: {
    duration: string;
    percent: number;
  }[];
};

export type CreateSimpleClusterData = {
  clusterTypes: {
    description: string;
    icon: typeof NvidiaIcon;
    id: string;
    name: string;
    disabled?: boolean;
    validate?: (values: Partial<ClusterForm>) => boolean;
  }[];
  connectivityTiers: {
    id: string;
    downloadSpeedMbps: number;
    name: string;
    uploadSpeedMbps: number;
    disabled?: boolean;
  }[];
  durations: {
    duration: string;
    percentage: number;
  }[];
  locations: ClusterLocation[];
  services: {
    description: string;
    icon: typeof NvidiaIcon;
    id: string;
    name: string;
    disabled: boolean;
    unavailable?: boolean;
  }[];
  suppliers: {
    busyPercent: number;
    description: string;
    icon: React.ReactNode;
    id: string;
    name: string;
    disabled?: boolean;
    unavailable?: boolean;
    validate?: (values: Partial<ClusterForm>) => boolean;
  }[];
  hardwareTypes: {
    value: string;
    label: string;
  }[];
  hardwareManufacturers: {
    value: string;
    label: string;
    icon: typeof NvidiaIcon;
  }[];
  hardwares: ClusterHardwareMap;
  hardwareList: (ClusterHardware | DynamicSupplyHardware)[];
  securityCompliances: {
    icon: typeof NvidiaIcon;
    id: string;
    name: string;
    disabled: boolean;
    unavailable?: boolean;
  }[];
};

export type CreateClusterData = {
  clusterTypes: {
    description: string;
    icon: typeof NvidiaIcon;
    id: string;
    name: string;
    disabled?: boolean;
    validate?: (values: Partial<ClusterForm>) => boolean;
  }[];
  connectivityTiers: {
    id: string;
    downloadSpeedMbps: number;
    name: string;
    uploadSpeedMbps: number;
    disabled?: boolean;
  }[];
  durations: {
    duration: string;
    percentage: number;
  }[];
  locations: ClusterLocation[];
  services: {
    description: string;
    icon: typeof NvidiaIcon;
    id: string;
    name: string;
    disabled: boolean;
    unavailable?: boolean;
  }[];
  suppliers: {
    busyPercent: number;
    description: string;
    icon: React.ReactNode;
    id: string;
    name: string;
    disabled?: boolean;
    unavailable?: boolean;
    validate?: (values: Partial<ClusterForm>) => boolean;
  }[];
  hardwareTypes: {
    value: string;
    label: string;
  }[];
  hardwareManufacturers: {
    value: string;
    label: string;
    icon: typeof NvidiaIcon;
  }[];
  hardwares: ClusterHardwareMap;
  hardwareList: ClusterHardware[];
  sustainable: {
    validate: (values: Partial<ClusterForm>) => boolean;
  };
  securityCompliances: {
    icon: typeof NvidiaIcon;
    id: string;
    name: string;
    disabled: boolean;
    unavailable?: boolean;
  }[];
};

export type ClusterHardwareMap = {
  [hardwareType: string]:
    | {
        [hardwareManufacturer: string]: ClusterHardware[] | undefined;
      }
    | undefined;
};

export type ClusterHardware = {
  id: string;
  available: number;
  hardwareManufacturer?: string;
  hardwareManufacturerIcon?: typeof NvidiaIcon;
  hardwareManufacturerColor?: string;
  hardwareName: string;
  name?: string;
  busyPercent: number;
  price: number;
  total: number;
  disabled?: boolean;
  type: string;
};

export type ClusterLocation = {
  id: string;
  name: string;
  icon?: typeof NvidiaIcon;
  busyPercentage?: number;
  description?: string;
  code?: string;
  iso2?: string;
  disabled?: boolean;
};

export type MenuItem = {
  value: string;
  label: string;
  icon?: typeof NvidiaIcon;
  /** Sidebar-only mark (e.g. Figma cloud / intelligence glyph); uses `currentColor`. */
  sidebarIcon?: typeof NvidiaIcon;
  href?: string;
  /** Nested links shown when expanded in the sidebar (e.g. Managed Services). */
  subMenuItems?: MenuItem[];
  analyticEvent?: AnalyticsEventType;
  disabled?: boolean;
  className?: string;
  active?: boolean;
  requiresLogin?: boolean;
  showInDelayedLogin?: boolean;
};

export type Tool = {
  value: string;
  label: string;
  icon: typeof NvidiaIcon;
  /** Sidebar-only mark; uses `currentColor`. */
  sidebarIcon?: typeof NvidiaIcon;
  href: string;
  prefix: string;
  logo?: typeof NvidiaIcon;
  logoLight?: typeof NvidiaIcon;
  logoImage?: string;
  bg?: string;
  theme?: "light" | "dark";
  menuItems?: MenuItem[];
};

export type InferenceType = {
  id: string;
  name: string;
  hasIncident: boolean;
  hardwareIcon?: typeof NvidiaIcon;
  hardwareManufacturerColor: string;
  hardwareName: string;
  hardwareQuantity: number;
  hardwareManufacturerIcon: typeof NvidiaIcon;
  hardwareManufacturer: string;
  computeHoursRemaining: string;
  computeHoursServed: string;
  percentCompleted: number;
  status: string;
  statusColor: string;
  statusMessage: string;
  statusLabel: string;
  statusValue: string;
  jupyterUrl?: string;
  vscodeUrl?: string;
  rayDashboardUrl?: string;
  startedAt: string;
  isBasedIO: boolean;
  computedDate: string;
};
export type InferenceSummary = {
  deviceId: string;
  isBasedAI: boolean;
  status: string;
  computedHours: string;
  createdAt: string;
  statusColor: string;
  statusMessage: string;
  link: string;
  bc8Link: string;
  inferencePrice: string;
  royaltyFee: string;
  networkFee: string;
  computedFee: string;
  hardwareQuantity: number;
  hardwareManufacturerColor: string;
  hardwareManufacturerIcon: typeof NvidiaIcon;
  hardwareName: string;
  clusterId: string;
  clusterOverload: number;
};

export type InferenceListResponse = {
  app_inference_txid_url: string;
  app_name: string;
  compute_duration: string;
  created_at: string;
  inference_id: string;
  requested_by: string;
  status: string;
  supplier: InferenceDetailSupplierResponse;
  href?: string;
};

export type InferenceDetailResponse = {
  app_inference_price: number;
  app_inference_txid_url: string;
  app_name: string;
  cluster_id: string;
  compute_duration: string;
  created_at: string;
  inference_id: string;
  inference_verification_url: string;
  io_network_fee: number;
  io_network_fee_txid_url: string;
  model_owner_royalty_fee: number;
  model_owner_royalty_fee_txid_url: string;
  requested_by: string;
  compute_cost: number;
  status: string;
  compute_cost_txid_url: string;
  supplier: InferenceDetailSupplierResponse;
  href?: string;
};

export type InferenceDetailSupplierResponse = {
  icon: string;
  name: string;
  busy_percent: number;
  is_active: boolean;
};

export type InferenceDetail = {
  appInferencePrice: number;
  appInferenceTxidUrl: string;
  appName: string;
  clusterId: string;
  computeDuration: string;
  createdAt: string;
  inferenceId: string;
  inferenceVerificationUrl: string;
  ioNetworkFee: number;
  ioNetworkFeeTxidUrl: string;
  modelOwnerRoyaltyFee: number;
  modelOwnerRoyaltyFeeTxidUrl: string;
  requestedBy: string;
  computeCost: number;
  status: string;
  computeCostTxidUrl: string;
  supplierName?: string;
  supplierIcon?: typeof NvidiaIcon;
  supplierIconColor?: string;
};

export type InferenceStats = {
  total_inference_count: number;
  total_inference_cost: number;
  total_inferences_today: number;
  total_inferences_today_cost: number;
};

export type DeviceResponse = {
  brand_icon: string;
  device_id: number;
  device_name: string;
  hardware: string;
  hardware_name?: string;
  cleanup_required: boolean | null;
  hardware_quantity: number;
  operating_system: string;
  status: string;
  status_duration: string;
  brand_name: string;
  is_working: boolean;
  is_baremetal: boolean;
  is_k8s: boolean;
  last_audit_successful: boolean | null;
  last_challenge_successful: boolean | null;
  readiness_info: {
    readiness: string;
    info: string;
  };
  is_external: boolean;
  block_rewards_ready: boolean;
};

export type Device = {
  id: string;
  name: string;
  isBaremetal: boolean;
  isKubernetes: boolean;
  isRecyclingRequired: boolean;
  operatingSystem: string;
  status: DeviceStatusType;
  statusLabel: string;
  statusColor: string;
  statusDuration: React.ReactNode;
  statusValue: string;
  networkMapLabel: string;
  hardwareName: string;
  hardwareQuantity: number;
  hardwareIcon: typeof NvidiaIcon;
  hardwareManufacturerIcon: typeof NvidiaIcon;
  hardwareManufacturer: string;
  hardwareManufacturerColor: string;
  isWorking: boolean;
  upFor?: string;
  href?: string;
  isVerified: boolean | null;
  isChecked: boolean | null;
  readinessInfo: {
    readiness: string;
    info: string;
  };
  isExternal: boolean;
  blockRewardsReady: boolean;
};

export type DeviceDetailResponse = {
  device_id: string;
  hardware_quantity: number;
  security_soc2: boolean;
  is_baremetal: boolean;
  is_k8s: boolean;
  location_name: string;
  location_icon: string;
  iso2: string;
  brand_icon: string;
  hardware_name: string;
  download_speed_mbps: number;
  upload_speed_mbps: number;
  base_tier_name: string;
  jobs: unknown[];
  down_percentage: number;
  downtime_by_date: {
    [k: string]: {
      downtime: number;
      note: string;
    };
  };
  brand_name: string;
  supplier_name: string;
  supplier?: {
    name: string;
    icon: string;
    is_active: boolean;
    busy_percent: number;
  };
  start_date: string;
  cleanup_required: boolean | null;
  device_type: string;
  operating_system: string;
  status: string;
  readiness_info: {
    readiness: string;
    info: string;
  };
  reward_pool: string;
};

export type DeviceDetail = {
  status: string;
  id: string;
  isRecyclingRequired: boolean;
  brandIcon: string;
  downPercentage: number | null;
  upPercentage: number | null;
  downtimeByDate: {
    [k: string]: {
      downtime: number;
      note: string;
    };
  } | null;
  hardwareName: string;
  hardwareQuantity: number;
  hardwareOSIcon?: typeof NvidiaIcon;
  hardwareManufacturerIcon: typeof NvidiaIcon;
  hardwareManufacturer: string;
  hardwareManufacturerColor: string;
  locationIcon: string;
  locationName: string;
  locationCode: string;
  securitySoc2: boolean;
  isBaremetal: boolean;
  isKubernetes: boolean;
  connectivityTierName: string;
  supplierName?: string;
  supplierIcon?: typeof NvidiaIcon;
  supplierIconColor?: string;
  createdAt: string;
  deviceType: string;
  operatingSystem: string;
  operatingSystemIcon?: typeof NvidiaIcon;
  readinessInfo: {
    readiness: string;
    info: string;
  };
  rewardPool: string;
};

export type DeviceNotificationResponse = {
  type: string;
  created_at: string;
  device_id: string;
  id: number;
  text: string;
};

export type DeviceNotification = {
  id: string;
  type: "success" | "error" | "info" | "alert";
  createdAt: string;
  deviceId: string;
  text: string;
  icon: typeof NvidiaIcon;
  color: string;
  gradient?: string;
};

export type DeviceServiceResponse = {
  id: number;
  description: string;
  icon: string;
  is_running: boolean;
  name: string;
  status: string;
};

export type DeviceService = {
  id: string;
  description: string;
  icon: typeof NvidiaIcon;
  isRunning: boolean;
  name: string;
  status: string;
};

export type DeviceHistoricalJobResponse = {
  for_id: string;
  device_id: string;
  hardware_name: string;
  brand_name: string;
  status: string;
  start_time: string;
  end_time: string;
  total_hire_rate: number;
  earned: number;
  slashed: number;
  compute_hours_served: number;
  compute_hours_hired: number;
  icon: string;
  currency: "usdc" | "iocoin";
  verification_job?: boolean;
};

export type DeviceJobResponse = {
  id?: string;
  for: string;
  for_id: string;
  resource_id: string;
  device_id: string;
  start_time: string;
  end_time: string;
  uptime_percent: number;
  total_hire_rate: number;
  slashed: number;
  earned: number;
  status: string;
  earning_claim_txid_url: string;
  created_at: string;
  updated_at: string;
  compute_hours_served: string;
  compute_hours_hired: string;
  hardware_name?: string;
  brand_name?: string;
  currency: "usdc" | "iocoin";
  verification_job?: boolean;
};

export type DeviceJob = {
  computeHoursHired: string;
  computeHoursServed: string;
  createdAt: string;
  deviceId: string;
  forId: string;
  resourceId: string;
  earned: number;
  earningClaimTxidUrl: string;
  endTime: string;
  id: string;
  slashed: number;
  startTime: string;
  status: string;
  totalHireRate: number;
  updatedAt: string;
  uptimePercent: number;
  currency: "usdc" | "iocoin";
  verificationJob: boolean;
};

export type DeviceHistoricalJob = {
  id: string;
  forId: string;
  deviceId: string;
  status: string;
  startTime: string;
  endTime: string;
  totalHireRate: number;
  earned: number;
  slashed: number;
  computeHoursServed: number;
  computeHoursHired: number;
  uptimePercent: number;
  hardwareName: string;
  hardwareManufacturerIcon: typeof NvidiaIcon;
  hardwareManufacturer: string;
  hardwareManufacturerColor: string;
  currency: "usdc" | "iocoin";
  verificationJob: boolean;
};

export type DeviceSummaryResponse = {
  device_id: string;
  device_name: string;
  status: string;
  status_duration: string;
  is_working: boolean;
  total_compute_hours_served: number;
  total_jobs: number;
  total_earnings: number;
  total_slashed_earning: number;
  download_speed_mbps: number;
  upload_speed_mbps: number;
  connectivity_tier: number;
  down_percentage: number;
  last_challenge_successful: boolean | null;
  last_audit_successful: boolean | null;
  total_earnings_io_coin?: number;
  total_slahed_io_coin?: number;
  downtime_by_date: {
    [k: string]: {
      downtime: number;
      note: string;
    };
  };
};

export type DeviceSummary = {
  name: string;
  connectivityTier: number;
  deviceId: string;
  downPercentage: number;
  downloadSpeedMbps: number;
  downtimeByDate: {
    [k: string]: {
      downtime: number;
      note: string;
    };
  };
  totalComputeHoursServed: number;
  totalEarnings: number;
  totalJobs: number;
  totalSlashedEarning: number;
  uploadSpeedMbps: number;
  status: DeviceStatusType;
  statusColor: string;
  statusValue: string;
  statusDuration: React.ReactNode;
  statusLabel: string;
  isWorking: boolean;
  isVerified: boolean | null;
  isChecked: boolean | null;
  totalEarnedIoCoin?: number;
  totalSlashedIoCoin?: number;
};

export type NormalEnvKeys = {
  [K in number as `env_normal_key_${K}`]: string;
};
export type NormalEnvValues = {
  [K in number as `env_normal_value_${K}`]: string;
};

export type SecretEnvKeys = {
  [K in number as `env_private_key_${K}`]: string;
};
export type SecretEnvValues = {
  [K in number as `env_private_value_${K}`]: string;
};

export type sshNameKeyType = {
  id: string;
  key: string;
  value: string;
};

export const GPU_UNAVAILABILITY_POLICIES = [
  "full_refund", // current behaviour, fail the resource and do full refund
  "partial_refund", // keep what was booked, refund prepaid for what failed
  "extend_booked" // keep what was booked, convert what was paid for failed into duration of successful bookings (example: tried to book 2 vms for 1 hour, was able to book only one => make it run for 2 hours)
] as const;
export type GpuUnavailabilityPolicy = (typeof GPU_UNAVAILABILITY_POLICIES)[number];
export const DEFAULT_GPU_UNAVAILABILITY_POLICY: GpuUnavailabilityPolicy = "extend_booked";

export type SimpleClusterForm = {
  clusterType: string;
  name: string;
  supplier: string;
  startCommand?: string;
  securityCompliance: string;
  service: string;
  connectivityTier: string;
  durationQuantity?: number;
  additionalService?: string[];
  durationType: string;
  hardware: string;
  hardwareQty?: Record<string, number>;
  hardwareManufacturer: string;
  hardwareType: string;
  hardwareQuantity?: number;
  locations: string[];
  sustainable?: boolean;
  nvLink?: boolean;
  k8sOperators?: string;
  sshKeyName?: string;
  sshKey?: string;
  createClusterData?: CreateSimpleClusterData;
  hardwareInfo?: ClusterHardwareInfo;
  clusterId?: string;
  masterNode?: string;
  currency: "iocoin" | "usdc" | "credits" | "stripe";
  priceUpdateTrigger?: number;
  resourceId?: string;
  containerImage?: string;
  containerRecipe?: string;
  command?: string;
  replicaCount?: string;
  trafficPort?: number;
  /** HTTP path for container health checks; empty omits health_check from the API payload. */
  healthCheckPath?: string;
  /** How often the platform probes the path (seconds); API default 10s, range 5s–10m. */
  healthCheckIntervalSeconds?: number;
  /** Max wait per probe before failure (seconds); API default 10s, range 1s–10m. */
  healthCheckTimeoutSeconds?: number;
  /** Warm-up window before failed probes count (seconds); API default 60s, range 10s–1h. */
  healthCheckGracePeriodSeconds?: number;
  username?: string;
  secret?: string;
  isPrivate?: boolean;
  deploymentId?: string;
  containerDeploymentId?: string;
  sshType?: string;
  sshKeys?: sshNameKeyType[];
  /** Account SSH key IDs checked for this deploy (persisted in saved deployment configs). */
  selectedSshKeyIds?: string[];
  githubIds?: string;
  vmImageType?: string;
  isConfidentialCompute?: boolean;
  gpusPerContainer?: number;
  /**
   * External-supply CaaS only. Behaviour when the requested GPUs cannot all be
   * placed:
   * - "partial_refund": run what we can, refund the containers that fail.
   * - "extend_booked": run what we can, give failed containers' booked time to
   *   the survivors (no refund, longer runtime; default).
   * - "full_refund": tear down the whole cluster (all-or-nothing) and refund in full.
   */
  gpuUnavailabilityPolicy?: GpuUnavailabilityPolicy;
  totalContainers?: number;
  amountPaid?: number;
  gpusPerNode?: string;
  gpuSearch?: string;
  gpuFilters?: Partial<DynamicSupplyHardwareFiltersResponse> | null;
  gpuShowUnavailable?: boolean;
  /** @deprecated Use gpuShowUnavailable (inverted). Kept for persisted-state migration. */
  gpuShowOnlyAvailable?: boolean;
  gpuSort?: "recommended" | "price_asc" | "price_desc" | "vram_asc" | "vram_desc";
  networkServices?: {
    id: string;
    name: string;
    port: number;
    protocol: "tcp" | "udp";
    whitelistIps: string[];
  }[];
} & SecretEnvKeys &
  SecretEnvValues &
  NormalEnvKeys &
  NormalEnvValues;

export type ClusterForm = {
  masterEnvironments?: string;
  clusterType: string;
  name: string;
  supplier: string;
  securityCompliance: string;
  service: string;
  connectivityTier: string;
  durationQuantity?: number;
  durationType: string;
  hardware: string;
  hardwareManufacturer: string;
  hardwareType: string;
  hardwareQuantity?: number;
  locations: string[];
  sustainable?: boolean;
  nvLink?: boolean;
  k8sOperators?: string;
  sshKeyName?: string;
  sshKey?: string;
  createClusterData?: CreateClusterData;
  hardwareInfo?: ClusterHardwareInfo;
  clusterId?: string;
  masterNode?: string;
  currency: "iocoin" | "usdc" | "credits" | "stripe";
  priceUpdateTrigger?: number;
  resourceId?: string;
  sshType?: string;
};

export type ModelsResponse = {
  category: string;
  creator_organization: string;
  description: string;
  display_name: string;
  hardware_label: string;
};

export type Model = {
  category: string;
  creator_organization: string;
  description: string;
  display_name: string;
  hardware_label: string;
};

export type LiveHardwarePricingResponse = {
  brand_name: string;
  hardware_name: string;
  id: number;
  price: number;
  total: number;
  hired: number;
  idle: number;
  is_trending: boolean;
  baremetal_price: number;
  caas_price: number;
  kubernetes_price: number;
  vmaas_price: number;
};

export type LiveHardwarePricing = {
  id: string;
  hardwareIcon: typeof NvidiaIcon;
  hardwareManufacturerColor: string;
  hardwareName: string;
  hardwareQuantity: number;
  hardwareManufacturerIcon: typeof NvidiaIcon;
  hardwareManufacturer: string;
  busyPercent: number;
  price: number;
  priceChange: number;
  total: number;
  hired: number;
  idle: number;
  isTrending: boolean;
  baremetalPrice: number;
  caasPrice: number;
  kubernetesPrice: number;
  vmaasPrice: number;
};

export type NetworkInfoSupplierResponse = {
  active_cpu: number;
  active_gpu: number;
  id: number;
  name: string;
  total_cpu: number;
  total_gpu: number;
  verified_cpu: number;
  verified_gpu: number;
};

export type NetworkInfoSupplier = {
  activeCpu: number;
  activeGpu: number;
  id: string;
  name: string;
  totalCpu: number;
  totalGpu: number;
  icon: typeof NvidiaIcon;
  iconColor: string;
  verifiedCpu: number;
  verifiedGpu: number;
  clusterReadyGpu: number;
  tooltip: string;
};

export type Filters = {
  [k: string]: string[] | undefined;
};

export type FilterGroup = {
  value: string;
  label: string;
  items: {
    value: string;
    label: string;
    icon: typeof NvidiaIcon;
    iconHeight?: number;
  }[];
  icon: typeof NvidiaIcon;
};

export type SimpleClusterHardwareInfoResponse = {
  available_quantities: number[];
  available_replica_count?: number[];
  avg: number;
  avg_iocoin: number;
  conversion_rate: number;
  currency_conversion_fee_percent: number;
  discount: number;
  gross_cost: number;
  ionet_fee: number;
  ionet_fee_percent: number;
  qty: number;
  hardware_qty?: number;
  total_cost: number;
  transaction: string;
  provider_fee?: number;
  /** Minimum paid (non-promotional) credit balance required to deploy. 0 => no minimum. */
  min_non_promotional_credits_required?: number;
};

export type ClusterHardwareInfoResponse = {
  avg: 1.03138461538462;
  avg_iocoin: 1.03138461538462;
  qty: 13;
  currency_conversion_fee_percent: number;
  conversion_rate: number;
  ionet_fee_percent: number;
  currency_conversion_fee_precent: number;
  ionet_fee_precent: number;
  transaction: string;
  available_quantity?: {
    [key: string]: number[];
  };
  cost?: number;
  ionet_fee?: number;
  total_cost?: number;
  provider_fee?: number;
  /** Minimum paid (non-promotional) credit balance required to deploy. 0 => no minimum. */
  min_non_promotional_credits_required?: number;
};

export type ClusterHardwareInfo = {
  averagePerCard: number;
  ioAveragePerCard: number;
  quantity: number;
  availableQuantity?: ClusterHardwareInfoMap[];
  conversionRate: number;
  transaction: string;
  cost?: number;
  totalCost?: number;
  providerFee?: number;
  /** Minimum paid (non-promotional) credit balance required to deploy/extend. 0 => no minimum. */
  minNonPromotionalCreditsRequired?: number;
};

export type ClusterHardwareInfoMap = {
  label: string;
  value: string;
};

export type ClusterStats = {
  today_compute_hours_served: number;
  total_clusters_created: number;
  total_compute_hours_served: number;
  total_earnings: number;
  running_clusters: number;
};

export type BrandResponse = {
  name: string;
};

export type Brand = {
  value: string;
  label: string;
  icon: typeof NvidiaIcon;
};

export type UserNotificationResponse = {
  id: string;
  user_id: string;
  obj_id?: string;
  obj_type?: string;
  topic: string;
  message: string;
  context?: string;
  source: string;
  destinations: string[];
  priority: NotificationType;
  created_at: string;
  seen_at: string;
  sent_at: string;
  scheduled_for: string;
};

export type UserNotification = {
  createdAt: string;
  id: string;
  text: string;
  icon: typeof NvidiaIcon;
  color: string;
  gradient?: string;
  type: string;
  isRead?: boolean;
  label?: string;
  title?: string;
  objType?: string;
  objId?: string;
};

export type UserPayoutResponse = {
  solscan_url: string;
};

export type UserPayout = {
  solscanUrl: string;
};

export type Notification = {
  id: string;
  type?: string;
  message: string;
};

export type DeviceForm = {
  ip: string;
  port: number;
  name: string;
  supplier: string;
  operatingSystem: string;
  deviceType: string;
  workerType: string;
  isConnected: string;
  followBareMetal: boolean;
};

export type RunDeviceForm = {
  operatingSystem: string;
  deviceType: string;
};

export type VisibleError = {
  title?: string;
  message: string;
  redirectTo?: string;
  isPublic?: boolean;
};

export type UserTransactionResponse = {
  amount: number;
  created_at: string;
  fee: number;
  id: string;
  method: string;
  network: string;
  payment_settled: boolean;
  source_type: TransactionSourceType;
  source_id: string;
  status: string;
  txid_url: string;
  original_amount: number;
  ionet_fee_precent: number;
  currency_conversion_fee_precent: number;
  currency: "usdc" | "iocoin";
  device_name?: string;
  device_id?: string;
  compute_minutes_served?: number;
  job_id?: string;
  platform: "cloud" | "worker";
  payment_source?: string;
  cluster_name?: string;
  cluster_id?: string;
  is_payg_charge?: boolean;
};

export type UserTransaction = {
  amount: number;
  originalAmount: number;
  createdAt: string;
  fee: number;
  id: string;
  method: string;
  network: string;
  payment_settled: boolean;
  sourceType: TransactionSourceType;
  sourceId: string;
  status: string;
  txidUrl: string;
  ionetFeePrecent: number;
  currencyConversionFeePrecent: number;
  currency: "usdc" | "iocoin";
  workerName?: string;
  timeAmount?: number;
  jobId?: string;
  deviceId?: string;
  platform: "cloud" | "worker" | "intelligence";
  paymentSource?: string;
  clusterName?: string;
  clusterId?: string;
  isPaygCharge?: boolean;
};

export type PaginatedResponse<T> = {
  page: number;
  page_size: number;
  total_pages: number;
} & T;

export type ZustandDefinition<T> = (
  set: (partial: T | Partial<T> | ((state: T) => T | Partial<T>)) => void,
  get: () => T
) => T;

export type LocationCityResponse = {
  location: string;
  geo_coordinates: string;
  hardware_name: string;
  hardware_quantity: number;
};

export type LocationCity = {
  name: string;
  lat: number;
  lon: number;
  country: string;
};

export type LocationCityGeoJSON = {
  type: "Feature";
  properties: {
    cluster: false;
    country: string;
    name: string;
  };
  geometry: {
    type: "Point";
    coordinates: [number, number];
  };
  info?: {
    hardwareName?: string;
    hardwareQuantity?: number;
  };
};

export type LocationDeviceResponse = {
  city: string;
  device_id: string;
  geo_coordinates: string;
  status: string;
  brand_name: string;
  status_duration: string;
  hardware_name: string;
  hardware_quantity: number;
};

export type LocationDevice = Device & {
  country: string;
  city: string;
  isConnected: boolean;
  lat: number;
  lon: number;
};

export type ExtendClusterRentalForm = {
  durationType: "hourly" | "daily" | "weekly";
  durationQuantity: number;
  currency: "iocoin" | "usdc" | "credits" | "stripe";
};

export type GenerateBalanceLinkForm = {
  amount: number;
  amountType: string;
  paymentProvider: "iocoin" | "usdc" | "stripe" | "credits";
};

export type ClaimRewardsForm = {
  amount: number;
  amountType: string;
};

export enum LoginType {
  SUPABASE = "supabase"
}

export type ZoomControlType = {
  latitude: number;
  longitude: number;
  zoom: number;
};

export enum ClusterType {
  RAY = "ray",
  KUBERNETES = "kubernetes",
  CONTAINER = "container",
  MEGA = "mega",
  LUDWIG = "ludwig",
  RAY_APP = "ray-app",
  BARE_METAL = "bare-metal",
  BARE_METAL_REQUEST = "bare-metal-request",
  VMAAS = "vmaas",
  CONFIDENTIAL_COMPUTE = "confidential-compute"
}

export type DeviceEarningsResponse = {
  device_id: string;
  total_compute_hours: number;
  total_earnings: number;
  total_jobs: number;
  total_slashed: number;
  datewise_earnings_summary: DeviceDailyEarningsResponse[];
};

export type DeviceEarningsType = {
  deviceId: string;
  totalComputeHours: number;
  totalEarnings: number;
  totalJobs: number;
  totalSlashed: number;
  datewiseEarningsSummary: DeviceDailyEarningsType[];
};

export type DeviceMonthlyEarningsType = {
  month: number;
  earning: number;
  year: number;
};

export type DeviceDailyEarningsResponse = {
  earning: number;
  date_with_year: string;
};

export type DeviceDailyEarningsType = {
  earning: number;
  dateWithYear: string;
};

export type ClustersEarningsSummaryResponse = {
  date_month: string;
  total_earnings: number;
  daily_earnings: number;
};

export type ClustersEarningsSummary = {
  dateMonth: string;
  totalEarnings: number;
  dailyEarnings: number;
};

export type GraphDataType = { name: string; value: number; avgValue?: number };

export type clusterVariant =
  | "cluster"
  | "explorer"
  | "mega"
  | "kubernetes"
  | "bare-metal"
  | "confidential-compute"
  | "container"
  | "vmaas";

export type UserDevicesEarningsResponse = {
  user_id: string;
  total_compute_hours: number;
  total_earnings: number;
  total_jobs: number;
  total_slashed: number;
  year: number;
  monthly_earnings_summary?: DeviceMonthlyEarningsType[];
  daily_earnings_summary: DeviceDailyEarningsResponse[];
  total_earned_io_coin?: number;
  total_slashed_io_coin?: number;
};

export type UserDevicesEarningsType = {
  userId?: string;
  totalComputeHours?: number;
  totalEarnings?: number;
  totalJobs?: number;
  totalSlashed?: number;
  year?: number;
  dailyEarningsSummary: DeviceDailyEarningsType[];
  totalEarnedIoCoin?: number;
  totalSlashedIoCoin?: number;
};

export type DevicePotlType = {
  blockId: string;
  deviceId: string;
  status: string;
  timeAndDate: string;
  uptimePercentage: number;
  uptimeInMinutes: number;
  txidUrl: string;
};

export type DevicePotlResponseType = {
  block_id: string;
  device_id: string;
  status: string;
  time_and_date: string;
  uptime_percentage: number;
  uptime_in_minutes: number;
  txid_url: string;
};

export type DevicePowResponseType = {
  proof_id: string;
  block_id: string;
  status: string;
  time_and_date: string;
  complexity: number;
  hash_rate: number;
  compute_time: number;
  device_id: string;
  txid_url: string;
};

export type DevicePowType = {
  proofId: string;
  blockId: string;
  deviceId: string;
  status: string;
  timeAndDate: string;
  complexity: number;
  hashRate: number;
  computeTime: number;
  txidUrl: string;
};

export type DeviceBlockEarningsType = {
  deviceId: string;
  totalBlockRewards: number;
  totalBlocksEarned: number;
  totalBlocksFailed: number;
  totalBlocksMissed: number;
  datewiseEarningsSummary: BlockDailyEarnings[];
};

export type BlockDailyEarnings = {
  rewards: number;
  dateWithYear: string;
};

export type DeviceBlockEarningsResponseType = {
  device_id: string;
  total_block_rewards: number;
  total_blocks_earned: number;
  total_blocks_failed: number;
  total_blocks_missed: number;
  datewise_earnings_summary: BlockDailyEarningsResponseType[];
};

export type BlockDailyEarningsResponseType = {
  rewards: number;
  date_with_year: string;
};

export type DevicePowSummaryResponseType = {
  device_id: string;
  last_challenge_status: string;
  last_audit_successful: string;
  pow_jobs_passed: number;
  pow_jobs_failed: number;
};

export type DevicePowSummaryType = {
  deviceId: string;
  lastChallengeStatus: string;
  powJobsPassed: number;
  powJobsFailed: number;
};

export type DeviceStatusCountsType = {
  block_workers: StatusCountsType;
  pow: StatusCountsType;
  potl: StatusCountsType;
};

export type StatusCountsType = {
  all: number | null;
  success: number | null;
  failed: number | null;
};

export type UsereBlockEarningsResponse = {
  total_block_rewards: number;
  total_blocks_earned: number;
  total_blocks_failed: number;
  total_blocks_missed: number;
  datewise_earnings_summary: BlockDailyEarningsResponseType[];
};

export type UsereBlockEarningsType = {
  totalBlockRewards?: number;
  totalBlocksEarned?: number;
  totalBlocksFailed?: number;
  totalBlocksMissed?: number;
  datewiseEarningsSummary: BlockDailyEarnings[];
};

export type UserLatestBlockRewardsResponse = {
  sum_rewarded_for_user: number;
};

export type UserLatestBlockRewards = {
  sumRewardedForUser: number;
};

export type TransactionSourceType =
  | "cluster"
  | "baremetal"
  | "container"
  | "k8s_nomad"
  | "mega"
  | "payments"
  | "payouts"
  | "Settlement"
  | "promotion_credit"
  | "job"
  | "intelligence";

export type DeviceBlockRewardsChecklistRulesType = {
  check: boolean;
  reason: string;
};

export type DeviceBlockRewardsChecklistType = {
  device_id: string;
  rules: DeviceBlockRewardsChecklistRulesType[];
  block_rewards_ready: boolean;
};

export type FormattedDateTimeFunctionType = (dateTime: unknown) => string | undefined;

export type DeviceHardware = Hardware & {
  hardwareId: string;
  brandId: string;
  brandName: string;
  hardwareName: string;
};

export type CountryNameCodeType = {
  countryCode: string;
  countryName: string;
};

export type RegionNameCodeType = {
  regionCode: string;
  regionName: string;
  countries: CountryNameCodeType[];
};

export type CountryHardwareType = {
  name: string;
  type: string;
  total: number;
  hired: number;
  idle: number;
};

export type CountryHardwaresResponseType = {
  hardware: CountryHardwareType[];
  total_gpu: number;
  total_cpu: number;
  total_gpu_hired: number;
  total_gpu_idle: number;
  total_cpu_hired: number;
  total_cpu_idle: number;
};

export type CountryHardwaresType = {
  hardware: CountryHardwareType[];
  totalGpu: number;
  totalCpu: number;
  totalGpuHired: number;
  totalGpuIdle: number;
  totalCpuHired: number;
  totalCpuIdle: number;
};

export type AreaGraphDataType = {
  time: string;
  value: number;
  devices?: {
    hardwareId: string;
    hardwareName: string;
    deviceCount: number;
  }[];
};

export type ClusterHourlyEarningsResponseType = {
  start_datetime: string;
  end_datetime: string;
  hourly_earnings: number;
  total_earnings: number;
};

export type ClusterHourlyEarningsType = {
  startDatetime: string;
  endDatetime: string;
  hourlyEarnings: number;
  totalEarnings: number;
};

export type TransactionsMonthlyEarningsType = {
  month: string;
  monthlyEarnings: number;
};

export type DeviceInventoryResponseType = {
  hardware_name: string;
  num_of_gpus: number;
  region: string;
  connectivity_tier: number | number[];
  device_type: string;
};

export type DeviceInventoryType = {
  hardwareName: string;
  numOfGpus: number;
  region: string;
  connectivityTier?: {
    value: number;
    label: string;
    icon: string;
  }[];
  deviceType: string;
};

export type TneTransactionResponseType = {
  hash: string;
  tx_date: string;
  tx_type: string;
  amount: number;
  currency: string;
  related_transaction: string;
  cluster_id: string;
  payment_url: string;
};

export type TneTransactionType = {
  hash: string;
  txDate: string;
  txType: string;
  amount: number;
  currency: string;
  relatedTransaction?: string;
  hashLink: string;
  relatedTransactionLink?: string;
  clusterId: string;
  paymentUrl: string;
};

export type EpochStatus = "all" | "open" | "closing" | "closed";

export type EpochSummaryResponseType = {
  total_epochs?: number;
  total_device_hours?: number;
  epoch_rewards?: number;
  io_purchased?: number;
  total_burn?: number;
  overall_total_burn?: number;
};

export type EpochSummaryType = {
  totalEpochs: number;
  totalDeviceHours: number;
  epochRewards: number;
  ioPurchased: number;
  totalBurn: number;
  overallTotalBurn?: number;
};

export type EpochRewardsSummaryResponseType = {
  total_coins_distributed?: number | null;
  todays_coins_distributed?: number | null;
  total_epochs_completed?: number | null;
  next_epoch_start_minutes?: number | null;
  total_unique_suppliers_paid?: number | null;
  todays_unique_suppliers_paid?: number | null;
};

export type EpochRewardsByDeviceClassResponseType = {
  total_io?: number | null;
  models?: {
    model_name?: string | null;
    device_count?: number | null;
    gpus_per_device?: number | null;
    supplier_count?: number | null;
  }[];
};

export type EpochRewardsSummaryApiResponseType = {
  status?: string;
  data?: {
    summary?: EpochRewardsSummaryResponseType | null;
    rewards_by_device_class?: EpochRewardsByDeviceClassResponseType | null;
  } | null;
};

export type EpochRewardsSummaryType = {
  totalDistributed?: number;
  todayDistributed?: number;
  totalCompleted?: number;
  totalSuppliersPaid?: number;
  todaySuppliersPaid?: number;
  nextEpochStartMinutes: number | null;
  rewardsByDeviceClass?: {
    totalIo?: number;
    models: EpochDeviceSplitModel[];
  };
};

export type EpochRewardRecordApiType = {
  epoch_id?: string;
  status?: string | null;
  epoch_start?: string | null;
  epoch_finish?: string | null;
  total_reward?: number | null;
  total_devices?: number | null;
  gpu_devices?: number | null;
  cpu_devices?: number | null;
  pending_count?: number | null;
  sent_count?: number | null;
  failed_count?: number | null;
  skipped_count?: number | null;
  epoch_signature?: string | null;
  burn_amount?: number | null;
  total_gpus?: number | null;
};

export type EpochRewardsListApiResponseType = {
  status?: string;
  data?: {
    total?: number | null;
    rewards?: EpochRewardRecordApiType[] | null;
  } | null;
};

export type EpochRewardListItemType = {
  id: string;
  status: string;
  epochId: string;
  epochStart: string;
  epochFinish: string;
  epochRewardsEmission: number;
  totalDevices: number;
  totalGpus: number;
  gpuDevices: number;
  cpuDevices: number;
  nominated: number;
  succeeded: number;
  failed: number;
  pendingCount: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  epochSignature: string;
  burnAmount: number;
  href?: string;
};

export type EpochResponseType = {
  status?: string;
  epoch_id?: string;
  start?: string;
  finish?: string;
  devices?: number;
  gpus?: number;
  epoch_rewards?: number;
  io_repurchased?: number;
  supplier_debt_paid?: number;
  supplier_debt_pending?: number;
  epoch_signature?: string | null;
  burn_amount?: number | null;
};

export type EpochType = {
  status: string;
  epochId: string;
  start: string;
  finish: string;
  devices: number;
  gpus: number;
  epochRewards: number;
  ioRepurchased: number;
  supplierDebtPaid: number;
  supplierDebtPending: number;
  epochSignature?: string | null;
  burnAmount?: number | null;
  href?: string;
};

export type EpochsDataResponseType = {
  summary?: EpochSummaryResponseType;
  epochs?: EpochResponseType[];
};

export type EpochsApiResponseType = {
  status: string;
  data?: EpochsDataResponseType;
  pagination?: {
    total_items?: number;
  };
};

export type EpochsFetchFilters = {
  epochFrom: string;
  epochTo: string;
  supplierId: string;
  clientId: string;
  status: EpochStatus;
};

export type EpochsFetchResponseType = {
  status: string;
  summary: EpochSummaryType;
  epochs: EpochType[];
  resultCount?: number;
};

export type EpochsFetchRequestType = EpochsFetchFilters & {
  page?: number;
  limit?: number;
};

export type EpochsStoreState = {
  filters: EpochsFetchFilters;
  appliedFilters: EpochsFetchFilters;
  summary?: EpochSummaryType;
  epochs: EpochType[];
  isLoading: boolean;
  isEpochDetailsLoading: boolean;
  error?: Error;
  epochDetailsError?: Error;
  epochDetailsNotFoundById: Record<string, boolean>;
  epochDetailsById: Record<string, EpochDetails>;
  validationErrors: Partial<Record<keyof EpochsFetchFilters, string>>;
  hasFetched: boolean;
  setField: <K extends keyof EpochsFetchFilters>(field: K, value: EpochsFetchFilters[K]) => void;
  resetFilters: () => void;
  fetchEpochsRequest: (filters: EpochsFetchRequestType) => Promise<EpochsFetchResponseType>;
  fetchEpochs: () => Promise<EpochsFetchResponseType | undefined>;
  fetchEpochDetails: (epochId: string) => Promise<EpochDetails | undefined>;
};

export type EpochDetailsPayload = {
  epoch_number?: string;
  started_at?: string;
  closed_at?: string | null;
  status?: string;
  active_devices?: number | null;
  /** Total allocated to suppliers in the epoch. Not yet served by the backend. */
  supplier_earnings_usd?: number | null;
  epoch_rewards_io?: number | null;
  epoch_rewards_usd?: number | null;
  repurchased_io_amount?: number | null;
  repurchased_usd_amount?: number | null;
  io_paid_out?: number | null;
  usd_paid_out?: number | null;
  open_debt?: number | null;
  epoch_signature?: string | null;
  burn_amount?: number | null;
  gpus?: number | null;
  active_gpus?: number | null;
  burn_transaction?: string | null;
};

export type EpochDetailsResponse = {
  status: string;
  data?: {
    details?: EpochDetailsPayload;
    device_split?: {
      device_count?: number;
      models?: {
        model_name?: string;
        share?: number;
        device_count?: number;
        /** Total GPUs for the model. Replaced `gpus_per_device` on the API. */
        gpu_count?: number;
        gpus_per_device?: number;
        supplier_count?: number;
      }[];
    };
    reward_records?: {
      device_id?: string;
      supplier_id?: string;
      model?: string;
      reward?: number;
      tx_id?: string | null;
    }[];
    supplier_payout_records?: {
      supplier_id?: string;
      client_id?: string;
      cluster_id?: string;
      transaction_type?: string;
      amount_usd?: number;
      amount_io?: number | null;
      tx_id?: string | null;
    }[];
    protocol_fee_transaction?: {
      epoch_id?: string;
      amount_io?: number | null;
      tx_id?: string | null;
    } | null;
    burn_transaction?: {
      epoch_id?: string;
      amount_io?: number | null;
      tx_id?: string | null;
    } | null;
  };
};

export type EpochDeviceSplitModel = {
  modelName: string;
  deviceCount: number;
  gpuPerDevice: number;
  /** Total GPUs as reported by the API; falls back to deviceCount × gpuPerDevice. */
  gpuCount?: number;
  supplierCount: number;
};

export type EpochDeviceSplit = {
  deviceCount: number;
  models: EpochDeviceSplitModel[];
};

export type EpochRewardRecord = {
  deviceId: string;
  supplierId: string;
  model: string;
  reward?: number;
  txId?: string;
};

export type EpochSupplierPayoutRecord = {
  supplierId: string;
  clientId: string;
  clusterId: string;
  transactionType: string;
  amountUsd?: number;
  amountIo?: number;
  txId?: string;
};

export type EpochTransactionRecord = {
  epochId: string;
  amountIo?: number;
  txId?: string;
};

export type EpochDetails = {
  epochNumber: string;
  startedAt: string;
  closedAt: string;
  status: string;
  activeDevices?: number;
  /** Total allocated to suppliers in the epoch. Not yet served by the backend. */
  supplierEarningsUsd?: number;
  epochRewardsIo?: number;
  epochRewardsUsd?: number;
  repurchasedIoAmount?: number;
  repurchasedUsdAmount?: number;
  ioPaidOut?: number;
  usdPaidOut?: number;
  openDebt?: number;
  deviceSplit?: EpochDeviceSplit;
  epochSignature?: string | null;
  rewardRecords: EpochRewardRecord[];
  supplierPayoutRecords: EpochSupplierPayoutRecord[];
  protocolFeeTransaction?: EpochTransactionRecord;
  burnTransaction?: EpochTransactionRecord;
  burnAmount?: number;
  gpus?: number;
  activeGpus?: number;
  burnTxnHash?: string;
};

export type ComputedHoursSummaryResponseType = {
  date: string;
  compute_hours: number;
  cumulative_compute_hours: number;
};

export type ComputedHoursSummaryType = {
  date: string;
  computeHours: number;
  cumulativeComputeHours: number;
};

export type RequestManagedClusterFormType = {
  regions: string[];
  connectivityTiers: number[];
  hardwareNames: string[];
  gpuQuantity?: number;
  contactEmail: string;
  telegramHandle?: string;
  neededBy?: Date;
  confidentialCompute?: boolean;
};

export type NetworkInfoResponse = {
  total: number;
  total_gpu: number;
  hired_gpu: number;
  idle_gpu: number;
  total_cpu: number;
  hired_cpu: number;
  idle_cpu: number;
};

export type NetworkInfo = {
  total: number;
  totalGpu: number;
  hiredGpu: number;
  idleGpu: number;
  totalCpu: number;
  hiredCpu: number;
  idleCpu: number;
};

export type DeviceSpeedTestResponseType = {
  status: string;
  start_time: string;
  download_speed_mbps: number;
  upload_speed_mbps: number;
  connectivity_tier: number;
};

export type DeviceSpeedTestType = {
  status: string;
  startTime: string;
  downloadSpeedMbps: number;
  uploadSpeedMbps: number;
  connectivityTier: number;
};

export type SuperAggDataResponseType = {
  active: number;
  passive: number;
  total: number;
  hardware: {
    hardware: string;
    brand: string;
    type: "GPU" | "CPU";
    active: number;
    passive: number;
    total: number;
  }[];
};

export type SuperAggDataType = {
  active: number;
  passive: number;
  total: number;
  hardwares: SuperAggDeviceType[];
};

export type SuperAggDeviceType = {
  name: string;
  brand: string;
  type: "GPU" | "CPU";
  active: number;
  passive: number;
  total: number;
  id: string;
  hardwareManufacturerIcon: typeof NvidiaIcon;
  hardwareManufacturerColor: string;
  hardwareManufacturer: string;
};

export type WorkerNetworkServiceType = {
  port: number;
  protocol: "tcp" | "udp";
  public_ip: string;
  public_port: number;
  srv_record: string;
  whitelist: string[];
};

export type HealthCheck = {
  path: string;
  /** Omitted on create/update — backend defaults apply when not sent. */
  interval?: string;
  timeout?: string;
  grace_period?: string;
};

export type ContainerDeployment = {
  id: string;
  status: "completed" | "running" | "failed" | "cancelled" | "pending";
  name: string;
  created_at: string;
  started_at: string;
  started_at_dt: string;
  finished_at: string;
  finished_at_dt: string;
  asset: "iocoin" | "usdc" | "credits" | "stripe";
  base_tier_name: string;
  download_speed_mbps: number;
  upload_speed_mbps: number;
  amount_paid: number;
  completed_percent: number;
  served: string;
  remaining: string;
  compute_minutes_served: number;
  compute_minutes_remaining: number;
  total_gpus: 1;
  total_containers: 1;
  gpus_per_container: 1;
  hardware_name: "H100 80GB HBM3";
  hardware_id: 224;
  brand_name: "NVIDIA";
  container_config: {
    entrypoint: string | null;
    env_variables: Record<string, string>;
    traffic_port: number;
    image_url: string;
    secret_env_keys: string[] | null;
    health_check: HealthCheck | null;
  };
  locations: ContainerLocation[];
};
